import type { ResolvedFigure } from '../model.js';
import { box, point, SceneBuilder, validateViewport } from '../scene-builder.js';
import { finishPrimitive, type PrimitiveSpec } from './contract.js';
import { d, displaceColumn, fits, type Frame, frameFor, header, note, textWidth, TYPE } from './kit.js';

export interface TimelineEvent {
  id: string;
  label: string;
  /** Elapsed time from the origin, in `unit`. Position is computed from this. */
  at: number;
  /** The source's own notation for the moment, e.g. '102:45:40'. */
  clock?: string;
  detail?: string;
}

export interface TimelineSpan {
  id: string;
  label: string;
  from: string;
  to: string;
}

export interface TimelineSpec extends PrimitiveSpec {
  family: 'primitive-timeline';
  view: 'sequence';
  subject: string;
  unit: string;
  /** In chronological order. Gaps are drawn to scale. */
  events: TimelineEvent[];
  spans?: TimelineSpan[];
  focusEventId?: string;
  title: string;
  subtitle: string;
}

export const timelineEventId = (id: string): string => `timeline.event.${id}`;
export const timelineSpanId = (id: string): string => `timeline.span.${id}`;
export const TIMELINE_RAIL_ID = 'timeline.rail';

interface Metrics {
  frame: Frame;
  spanLane: number;
  railX: number;
  labelX: number;
  labelW: number;
  railTop: number;
  railBottom: number;
  blockH: number;
  showGaps: boolean;
  footerY: number;
  required: number;
}

function metrics(spec: TimelineSpec): Metrics {
  const frame = frameFor(spec.viewport.width, spec.viewport.height);
  const spanLane = (spec.spans ?? []).length ? (frame.tier === 'compact' ? 74 : 92) : 0;
  const railX = frame.margin + spanLane + 24;
  const labelX = railX + 22;
  const labelW = frame.width - frame.margin - labelX;
  const railTop = frame.top + 20;
  const footerY = spec.viewport.height - 20;
  const railBottom = footerY - 26;
  const blockH = 34;
  const showGaps = frame.tier === 'wide';
  const labelWidth = showGaps ? labelW - 96 : labelW;
  return { frame, spanLane, railX, labelX, labelW: labelWidth, railTop, railBottom, blockH, showGaps, footerY, required: 380 };
}

/**
 * A timeline is a measured axis, not a list of steps in order.
 *
 * If the events were merely ordered, a numbered list would be a better figure.
 * What only a timeline can show is the *gaps*: that the famous moment and the
 * one everybody remembers next were six hours apart inside an eight-day
 * mission. So this compiler places every event by its elapsed time, never by its
 * index; when two moments crowd, the label moves and a leader keeps it tied to
 * the true instant, because moving the event itself would be a lie.
 */
export function compileTimeline(spec: TimelineSpec): ResolvedFigure {
  validateTimeline(spec);

  const builder = new SceneBuilder();
  const m = metrics(spec);
  const { frame } = m;
  const warnings: string[] = [];
  const first = spec.events[0]!.at;
  const last = spec.events[spec.events.length - 1]!.at;
  const span = last - first;
  const railLength = m.railBottom - m.railTop;
  const at = (value: number): number => m.railTop + ((value - first) / span) * railLength;

  header(builder, frame, 'timeline', `ELAPSED TIME · ${spec.subject.toUpperCase()}`, [
    `${format(span)} ${spec.unit.toUpperCase()} END TO END · GAPS DRAWN TO SCALE`,
    `${format(span)} ${spec.unit.toUpperCase()} · TO SCALE`,
  ]);

  builder.line(`${TIMELINE_RAIL_ID}.line`, TIMELINE_RAIL_ID, 'axis', point(m.railX, m.railTop), point(m.railX, m.railBottom));
  builder.text(`${TIMELINE_RAIL_ID}.unit`, TIMELINE_RAIL_ID, 'annotation', point(m.labelX, m.railTop - 12), `ELAPSED ${spec.unit.toUpperCase()}`, {
    size: TYPE.micro, mono: true, weight: 700, uppercase: true,
  });
  builder.anchor(`${TIMELINE_RAIL_ID}.start`, TIMELINE_RAIL_ID, point(m.railX, m.railTop), 'up');
  builder.anchor(`${TIMELINE_RAIL_ID}.end`, TIMELINE_RAIL_ID, point(m.railX, m.railBottom), 'down');
  builder.hit(TIMELINE_RAIL_ID, box(m.railX - 10, m.railTop, 20, railLength));
  if (m.showGaps) {
    builder.text(`${TIMELINE_RAIL_ID}.gap-head`, TIMELINE_RAIL_ID, 'annotation', point(frame.margin + frame.contentWidth, m.railTop - 12), 'SINCE PREVIOUS', {
      size: TYPE.micro, mono: true, weight: 700, align: 'end', uppercase: true,
    });
  }

  // Spans first: they are the measured intervals the events bracket.
  const eventById = new Map(spec.events.map((event) => [event.id, event]));
  (spec.spans ?? []).forEach((spanItem, index) => {
    const semanticId = timelineSpanId(spanItem.id);
    const from = eventById.get(spanItem.from)!;
    const to = eventById.get(spanItem.to)!;
    const y1 = at(from.at);
    const y2 = at(to.at);
    const laneX = frame.margin + 10 + index * 22;
    builder.line(`${semanticId}.measure`, semanticId, 'dimension', point(laneX, y1), point(laneX, y2), { markerStart: true, markerEnd: true });
    builder.line(`${semanticId}.extension.start`, semanticId, 'construction', point(laneX, y1), point(m.railX - 4, y1));
    builder.line(`${semanticId}.extension.end`, semanticId, 'construction', point(laneX, y2), point(m.railX - 4, y2));
    const duration = `${format(to.at - from.at)} ${spec.unit}`;
    const text = `${spanItem.label.toUpperCase()} · ${duration}`;
    builder.text(`${semanticId}.label`, semanticId, 'annotation', point(laneX - 7, (y1 + y2) / 2), text, {
      size: TYPE.micro, mono: true, weight: 700, align: 'middle', rotate: -90, uppercase: true,
    });
    if (!fits(text, TYPE.micro, Math.abs(y2 - y1), true, true)) {
      warnings.push(`The ${spanItem.label} band is shorter than its own label at this height.`);
    }
    builder.anchor(`${semanticId}.from`, semanticId, point(laneX, y1), 'up');
    builder.anchor(`${semanticId}.to`, semanticId, point(laneX, y2), 'down');
    builder.hit(semanticId, box(Math.max(0, laneX - 14), Math.min(y1, y2), 28, Math.abs(y2 - y1)));
    builder.relation(`${semanticId}.lasts`, 'lasts', timelineEventId(spanItem.from), timelineEventId(spanItem.to), `${spanItem.label} lasts ${duration}`);
  });

  // Labels are displaced; the pins never are.
  const preferred = spec.events.map((event) => at(event.at) - 12);
  const placed = displaceColumn(preferred, m.blockH + 6, m.railTop - 12, m.railBottom - m.blockH + 12);

  spec.events.forEach((event, index) => {
    const semanticId = timelineEventId(event.id);
    const y = at(event.at);
    const labelTop = placed[index]!;
    const active = event.id === spec.focusEventId;

    builder.rect(`${semanticId}.pin`, semanticId, active ? 'active' : 'event', box(m.railX - 5, y - 5, 10, 10), { radius: 5, label: event.label });
    builder.path(`${semanticId}.leader`, semanticId, 'annotation',
      `M ${d(m.railX + 6)} ${d(y)} L ${d(m.labelX - 10)} ${d(y)} L ${d(m.labelX - 10)} ${d(labelTop + 12)} L ${d(m.labelX - 3)} ${d(labelTop + 12)}`);

    const clock = event.clock ?? `${format(event.at)} ${spec.unit}`;
    builder.text(`${semanticId}.clock`, semanticId, 'annotation', point(m.labelX, labelTop + 11), clock, {
      size: TYPE.micro, mono: true, weight: 700,
    });
    builder.text(`${semanticId}.label`, semanticId, 'structure', point(m.labelX, labelTop + 27), event.label, {
      size: TYPE.label, mono: true, weight: 700,
    });
    if (event.detail && fits(event.detail, TYPE.micro, m.labelW - textWidth(clock, TYPE.micro, true) - 12, true)) {
      builder.text(`${semanticId}.detail`, semanticId, 'annotation', point(m.labelX + textWidth(clock, TYPE.micro, true) + 10, labelTop + 11), event.detail, {
        size: TYPE.micro, mono: true, weight: 500,
      });
    }
    builder.anchor(`${semanticId}.pin`, semanticId, point(m.railX, y), 'right');
    builder.anchor(`${semanticId}.label`, semanticId, point(m.labelX, labelTop + 12), 'left');
    builder.hit(semanticId, box(m.railX - 12, labelTop, m.labelW + 34, m.blockH));

    if (index > 0 && m.showGaps) {
      const previous = spec.events[index - 1]!;
      const gapText = `+${format(event.at - previous.at)} ${spec.unit}`;
      builder.text(`${semanticId}.gap`, semanticId, 'annotation', point(frame.margin + frame.contentWidth, labelTop + 27), gapText, {
        size: TYPE.micro, mono: true, weight: 700, align: 'end',
      });
    }
    if (index > 0) {
      const previous = spec.events[index - 1]!;
      builder.relation(
        `${semanticId}.after`,
        'follows',
        timelineEventId(previous.id),
        semanticId,
        `${event.label} follows ${previous.label} after ${format(event.at - previous.at)} ${spec.unit}`,
      );
    }
    if (Math.abs(labelTop + 12 - y) > 2) {
      builder.relation(`${semanticId}.displaced`, 'label-displaced', semanticId, TIMELINE_RAIL_ID, `${event.label} is drawn at ${format(event.at)} ${spec.unit}; only its label was moved`);
    }
  });

  const crowded = spec.events.filter((event, index) => index > 0 && at(event.at) - at(spec.events[index - 1]!.at) < m.blockH).length;
  if (crowded > 0) warnings.push(`${crowded} moments sit closer together than their labels; the labels moved, the pins did not.`);

  const notes = [
    `${spec.events.length} MOMENTS · DISTANCE ON THE RAIL IS ELAPSED TIME`,
    `${spec.events.length} MOMENTS · DISTANCE IS ELAPSED TIME`,
    `${spec.events.length} MOMENTS · TO SCALE`,
  ];
  note(builder, 'timeline.note', 'timeline', point(frame.margin, m.footerY), notes.find((candidate) => fits(candidate, TYPE.micro, frame.contentWidth, true, true)) ?? notes[notes.length - 1]!);

  return finishPrimitive(spec, builder, {
    title: spec.title,
    subtitle: spec.subtitle,
    state: spec.focusEventId ? `focus:${spec.focusEventId}` : 'overview',
    active: [spec.focusEventId ? timelineEventId(spec.focusEventId) : TIMELINE_RAIL_ID, ...(spec.emphasis ?? [])],
    warnings,
  });
}

export function validateTimeline(spec: TimelineSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.family !== 'primitive-timeline' || spec.view !== 'sequence') throw new Error('timeline: family and view are fixed');
  if (!spec.provenance.trim()) throw new Error('timeline: provenance is required');
  if (!spec.subject.trim() || !spec.unit.trim() || !spec.title.trim() || !spec.subtitle.trim()) throw new Error('timeline: subject, unit, title and subtitle are required');
  if (spec.events.length < 2 || spec.events.length > 8) throw new Error('timeline: require 2…8 events');
  const ids = new Set<string>();
  let previous = Number.NEGATIVE_INFINITY;
  for (const event of spec.events) {
    if (!/^[a-z][a-z0-9-]*$/.test(event.id)) throw new Error(`timeline: invalid event id ${event.id}`);
    if (ids.has(event.id)) throw new Error(`timeline: duplicate event id ${event.id}`);
    ids.add(event.id);
    if (!event.label.trim() || event.label.length > 22) throw new Error(`timeline: event ${event.id} needs a label of 1…22 characters`);
    if (!Number.isFinite(event.at)) throw new Error(`timeline: event ${event.id} needs an elapsed time`);
    if (event.at <= previous) throw new Error('timeline: events must be given in strictly increasing time order');
    previous = event.at;
  }
  const spanIds = new Set<string>();
  for (const span of spec.spans ?? []) {
    if (!/^[a-z][a-z0-9-]*$/.test(span.id)) throw new Error(`timeline: invalid span id ${span.id}`);
    if (spanIds.has(span.id)) throw new Error(`timeline: duplicate span id ${span.id}`);
    spanIds.add(span.id);
    if (!ids.has(span.from) || !ids.has(span.to)) throw new Error(`timeline: span ${span.id} names an event that does not exist`);
    const from = spec.events.findIndex((event) => event.id === span.from);
    const to = spec.events.findIndex((event) => event.id === span.to);
    if (to <= from) throw new Error(`timeline: span ${span.id} must run forward in time`);
    if (!span.label.trim() || span.label.length > 20) throw new Error(`timeline: span ${span.id} needs a label of 1…20 characters`);
  }
  if ((spec.spans ?? []).length > 3) throw new Error('timeline: at most three measured spans');
  if (spec.focusEventId && !ids.has(spec.focusEventId)) throw new Error('timeline: the focus event does not exist');
  const required = metrics(spec).required;
  if (spec.viewport.height < required) throw new Error(`timeline: this timeline needs at least ${Math.ceil(required)}px of height`);
  const minimumRail = spec.events.length * 40;
  if (spec.viewport.height - 120 < minimumRail) throw new Error(`timeline: ${spec.events.length} moments need at least ${minimumRail + 120}px of height`);
}

function format(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 10) / 10);
}
